import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Row,
  Col,
  Button,
  Spinner,
  Collapse,
  Modal,
  Badge,
  InputGroup,
  FormControl,
  Dropdown,
} from "react-bootstrap";
import axios from "axios";
import { 
  FaEye, 
  FaPlay, 
  FaPause, 
  FaSync, 
  FaCheck, 
  FaClock, 
  FaFilter, 
  FaTimes, 
  FaChevronDown, 
  FaChevronUp,
  FaSearch,
  FaRedo
} from "react-icons/fa";
import Swal from "sweetalert2";
import { BASE_URL } from "../constants";
import Pagination from "./common/Pagination";
import usePagination from "../hooks/usePagination";
import { theme } from "../styles/theme";

// Styles personnalisés
const customStyles = {
  filterButton: {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.primary}`,
    color: theme.colors.primary,
    '&:hover': {
      backgroundColor: theme.colors.primary,
      color: theme.colors.white,
    },
  },
  tableHeader: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  tableRow: {
    transition: theme.transition.default,
    '&:hover': {
      backgroundColor: 'rgba(37, 99, 235, 0.05)',
    },
  },
  actionButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: theme.borderRadius.full,
    transition: theme.transition.default,
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  filterSection: {
    backgroundColor: theme.colors.light,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.sm,
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  filterBadge: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    marginLeft: '0.5rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.secondary,
    },
  },
};

export default function CallsTable() {
  const [data, setData] = useState([]);
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    verdict: "",
    call_id: "",
    user_firstname: "",
    user_email: "",
    code_erreur: "",
    categorie: "",
    date: "",
  });
  const [errorCodes, setErrorCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  
  const {
    currentPage,
    totalPages,
    updatePagination,
    goToPage,
    hasNextPage,
    hasPrevPage,
    pageSize
  } = usePagination(1, 10);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  const toggleRow = useCallback((idx) => {
    setOpenRow(openRow === idx ? null : idx);
  }, [openRow]);

  const handleShowModal = (content, title) => {
    setModalContent(content);
    setModalTitle(title);
    setShowModal(true);
  };

  const handlePlayAudio = useCallback((audioUrl) => {
    if (!audioUrl) return;
    if (currentAudio && currentAudioUrl === audioUrl) {
      if (currentAudio.paused) {
        currentAudio.play();
      } else {
        currentAudio.pause();
      }
      return;
    }
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    const audio = new Audio(audioUrl);
    setAudioProgress(0);
    setAudioDuration(0);
    setCurrentAudio(audio);
    setCurrentAudioUrl(audioUrl);
    audio.play();
  }, [currentAudio, currentAudioUrl]);

  useEffect(() => {
    if (!currentAudio) return;
    const onTimeUpdate = () => setAudioProgress(currentAudio.currentTime || 0);
    const onLoadedMetadata = () => setAudioDuration(Number.isFinite(currentAudio.duration) ? currentAudio.duration : 0);
    const onEnded = () => {
      setCurrentAudio(null);
      setCurrentAudioUrl(null);
      setAudioProgress(0);
    };
    const onPlay = () => setAudioProgress(currentAudio.currentTime || 0);
    const onPause = () => setAudioProgress(currentAudio.currentTime || 0);

    currentAudio.addEventListener('timeupdate', onTimeUpdate);
    currentAudio.addEventListener('loadedmetadata', onLoadedMetadata);
    currentAudio.addEventListener('ended', onEnded);
    currentAudio.addEventListener('play', onPlay);
    currentAudio.addEventListener('pause', onPause);

    return () => {
      currentAudio.removeEventListener('timeupdate', onTimeUpdate);
      currentAudio.removeEventListener('loadedmetadata', onLoadedMetadata);
      currentAudio.removeEventListener('ended', onEnded);
      currentAudio.removeEventListener('play', onPlay);
      currentAudio.removeEventListener('pause', onPause);
      if (currentAudio) currentAudio.pause();
    };
  }, [currentAudio]);

  const handleSeek = useCallback((value) => {
    const v = parseFloat(value);
    if (currentAudio && Number.isFinite(v)) {
      currentAudio.currentTime = v;
      setAudioProgress(v);
    }
  }, [currentAudio]);

  const formatTime = useCallback((seconds) => {
    const s = Math.max(0, Math.floor(seconds || 0));
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  }, []);
  const handleCloseModal = () => setShowModal(false);

  const fetchAgents = async () => {
    try {
      const token = "66ccd6b118f2dc31be67e8e2bcfc0099da653662";
      const res = await axios.get("https://public-api.ringover.com/v2/users", {
        headers: { Authorization: token },
      });
      const agentFirstnames = res.data.list.map((user) => user.firstname);
      setAgents(agentFirstnames);
    } catch (error) {
      console.error("Erreur chargement agents :", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data.data); // [{id,name},...]
    } catch (error) {
      console.error("Erreur chargement catégories :", error);
    }
  };

  const fetchErrorCodes = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(`${BASE_URL}/promptgpts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const codes = res.data.map((item) => item.error_code);
      setErrorCodes(codes);
    } catch (error) {
      console.error("Erreur chargement codes :", error);
    }
  };

  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${BASE_URL}/analyses`, {
          params: { 
            ...filters, 
            page,
            per_page: pageSize 
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setData(res.data.data);
        updatePagination({
          currentPage: res.data.current_page,
          totalItems: res.data.total || res.data.data?.length || 0,
          totalPages: res.data.last_page || 1,
          pageSize: res.data.per_page || pageSize
        });
      } catch (error) {
        console.error("Erreur récupération API :", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, updatePagination]
  );

  useEffect(() => {
    fetchAgents();
    fetchErrorCodes();
    fetchCategories();
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({
      verdict: "",
      call_id: "",
      user_firstname: "",
      user_email: "",
      code_erreur: "",
      categorie: "",
      date: "",
    });
  };

  const handleRefreshRow = async (callId, index) => {
    const token = localStorage.getItem("auth_token");

    setData((prevData) =>
      prevData.map((row, i) =>
        i === index ? { ...row, loading: true } : row
      )
    );

    try {
      const res = await axios.post(
        `${BASE_URL}/transcriptions/analyse-transcription-deepseek`,
        { call_id: callId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedRow = res.data.analysis;
      console.log("updatedRow", updatedRow);

      setData((prevData) =>
        prevData.map((row, i) =>
          i === index ? { ...row, ...updatedRow, loading: false } : row
        )
      );

      Swal.fire({
        icon: "success",
        title: "Rescan terminé",
        text: `Rescan du call ${callId} terminé avec succès ✅`,
        confirmButtonColor: "#11b2e7",
      });
    } catch (error) {
      console.error("Erreur actualisation :", error);

      setData((prevData) =>
        prevData.map((row, i) =>
          i === index ? { ...row, loading: false } : row
        )
      );

      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: `Échec du rescan du call ${callId}.`,
        confirmButtonColor: "#d33",
      });
    }
  };

  const handlePageChange = useCallback((page) => {
    goToPage(page);
    fetchData(page);
  }, [fetchData, goToPage]);

  // Compteur de filtres actifs
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  // État pour contrôler la visibilité des filtres
  const [showFilters, setShowFilters] = useState(true);

  return (
    <div className="container-fluid p-3">
      {/* En-tête avec bouton de filtre */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Liste des appels</h2>
        <Button 
          variant="outline-primary" 
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center"
          style={customStyles.filterButton}
        >
          <FaFilter className="me-2" />
          Filtres
          {activeFiltersCount > 0 && (
            <Badge bg="primary" className="ms-2" style={customStyles.filterBadge}>
              {activeFiltersCount}
            </Badge>
          )}
          {showFilters ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
        </Button>
      </div>

      {/* Section des filtres */}
      <Collapse in={showFilters}>
        <div style={customStyles.filterSection}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="h5 mb-0">Filtres</h3>
            <div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleReset}
                className="me-2"
              >
                <FaRedo className="me-1" /> Réinitialiser
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => setShowFilters(false)}
              >
                <FaTimes className="me-1" /> Fermer
              </Button>
            </div>
          </div>
          
          <Form className="mb-3">
            <Row className="g-3 align-items-end">
              <Col md={1}>
                <Form.Group>
                  <Form.Label>Agent</Form.Label>
                  <Form.Control
                    as="select"
                    name="user_firstname"
                    value={filters.user_firstname}
                    onChange={handleFilterChange}
                  >
                    <option value="">Tous</option>
                    {agents.map((firstname, index) => (
                      <option key={index} value={firstname}>
                        {firstname}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={1}>
                <Form.Group>
                  <Form.Label>Type de vente</Form.Label>
                  <Form.Control
                    as="select"
                    name="user_lastname"
                    value={filters.user_lastname}
                    onChange={handleFilterChange}
                  >
                    <option value="">Tous</option>
                    <option value="AVV">AVV</option>
                    <option value="APV">APV</option>
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={1}>
                <Form.Group>
                  <Form.Label>Solutionné</Form.Label>
                  <Form.Control as="select" name="verdict" value={filters.verdict} onChange={handleFilterChange}>
                    <option value="">Tous</option>
                    <option value="1">Oui</option>
                    <option value="0">Non</option>
                  </Form.Control>
                </Form.Group>
              </Col>
                
              <Col md={1}>
                <Form.Group>
                  <Form.Label>Date</Form.Label>
                  <Form.Control type="date" name="date" value={filters.date} onChange={handleFilterChange} />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Catégorie</Form.Label>
                  <Form.Control
                    as="select"
                    name="categorie"
                    value={filters.categorie}
                    onChange={handleFilterChange}
                  >
                    <option value="">Toutes</option>
                    {categories.map((cat, index) => (
                      <option key={index} value={cat.categorie_name}>
                        {cat.categorie_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Code Erreur</Form.Label>
                  <Form.Control as="select" name="code_erreur" value={filters.code_erreur} onChange={handleFilterChange}>
                    <option value="">Tous</option>
                    {errorCodes.map((code, index) => (
                      <option key={index} value={code}>
                        {code}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Call ID</Form.Label>
                  <Form.Control type="text" placeholder="Call ID" name="call_id" value={filters.call_id} onChange={handleFilterChange} />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="text" placeholder="Email" name="user_email" value={filters.user_email} onChange={handleFilterChange} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </div>
      </Collapse>

      {loading ? (
        <div className="text-center my-5 py-5">
          <Spinner animation="border" variant="primary" className="me-2" />
          <span className="text-muted">Chargement des données...</span>
        </div>
      ) : data.length === 0 ? (
        <div className="text-center p-5 my-5">
          <div className="bg-light p-5 rounded-3">
            <div className="mb-3">
              <FaSearch size={48} className="text-muted mb-3" />
              <h4 className="text-muted">Aucune donnée trouvée</h4>
              <p className="text-muted">Essayez de modifier vos critères de recherche</p>
            </div>
            <Button 
              variant="outline-primary" 
              onClick={handleReset}
              className="mt-2"
            >
              <FaRedo className="me-2" /> Réinitialiser les filtres
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover responsive className="shadow-sm" style={{ minWidth: '1200px', '--bs-table-hover-bg': 'rgba(37, 99, 235, 0.03)' }}>
              <thead style={customStyles.tableHeader}>
              <tr>
                <th></th>
                <th>Call ID</th>
                <th>Prénom</th>
                <th>Type de vente</th>
                <th>Catégorie</th>
                <th>Email</th>
                <th>Erreurs</th>
                <th>Nom Complet</th>
                <th>Solutionnés</th>
                <th>Date Création</th>
                <th>Audio</th>
                <th>Tran Original</th>
                <th>Tran Traiter</th> {/* ✅ Nouvelle colonne */}
                <th>Actualiser</th>
                <th>Rescanné</th>
              </tr>
            </thead>
              <tbody>
              {data?.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tr>
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={() => toggleRow(idx)}
                        className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{ width: '28px', height: '28px' }}
                      >
                        {openRow === idx ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                      </Button>
                    </td>
                    <td>{row.call_id}</td>
                    <td>{row.user_firstname}</td>
                    <td>{row.user_lastname}</td>
                    <td>{row.categorie}</td>
                    <td>{row.user_email}</td>
                    <td>{row.errors?.length > 0 ? (
                      row.errors.map((error, errorIdx) => (
                        <Badge key={errorIdx} bg="danger" className="me-1">{error}</Badge>
                      ))
                    ) : (
                      <Badge bg="secondary">Aucune</Badge>
                    )}</td>
                    <td>{row.user_concat_name}</td>
                    <td
                      style={{
                        color: row.verdict === "1" ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {row.verdict === "1" ? "Oui" : "Non"}
                    </td>
                    <td>
                      {new Date(row.created_at).toLocaleDateString("fr-FR")}{" "}
                      {new Date(row.created_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      {row.record_path ? (
                        <Button
                          variant={currentAudioUrl === row.record_path && currentAudio && !currentAudio.paused ? 'outline-danger' : 'outline-primary'}
                          size="sm"
                          onClick={() => handlePlayAudio(row.record_path)}
                          className="rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                          style={{
                            ...customStyles.actionButton,
                            width: '28px',
                            height: '28px',
                          }}
                        >
                          {currentAudioUrl === row.record_path && currentAudio && !currentAudio.paused ? (
                            <FaPause size={12} />
                          ) : (
                            <FaPlay size={12} />
                          )}
                        </Button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                      {currentAudioUrl === row.record_path && currentAudio && (
                        <div className="mt-2" style={{ minWidth: '180px' }}>
                          <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem' }}>
                            <span>{formatTime(audioProgress)}</span>
                            <span>{formatTime(audioDuration)}</span>
                          </div>
                          <input
                            type="range"
                            className="form-range"
                            min={0}
                            max={audioDuration || 0}
                            step="0.1"
                            value={Math.min(audioProgress, audioDuration || 0)}
                            onChange={(e) => handleSeek(e.target.value)}
                            style={{
                              height: '24px',
                              accentColor: '#16a34a',
                              '--bs-form-range-track-bg': '#dcfce7',
                              '--bs-form-range-thumb-bg': '#16a34a',
                              '--bs-form-range-thumb-active-bg': '#15803d'
                            }}
                          />
                        </div>
                      )}
                    </td>

                    {/* 🔵 Voir transcription originale */}
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          handleShowModal(row.transcription, "Transcription Originale")
                        }
                        style={customStyles.actionButton}
                      >
                        <FaEye size={14} />
                      </Button>
                    </td>

                    {/* 🟢 Voir transcription traitée */}
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() =>
                          handleShowModal(row.transcription_traiter, "Transcription Traitée")
                        }
                        style={customStyles.actionButton}
                      >
                        <FaEye size={14} />
                      </Button>
                    </td>

                    {/* Actualiser */}
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      {row.loading ? (
                        <Spinner animation="border" size="sm" variant="info" />
                      ) : (
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleRefreshRow(row.call_id, idx)}
                          className="p-0 d-inline-flex align-items-center justify-content-center"
                          style={{
                            ...customStyles.actionButton,
                            width: '28px',
                            height: '28px',
                          }}
                          title="Actualiser"
                        >
                          <FaSync size={12} />
                        </Button>
                      )}
                    </td>

                    {/* Rescanné */}
                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                      {row.b_scanned === 1 ? (
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white" 
                             style={{ width: '24px', height: '24px' }}>
                          <FaCheck size={12} />
                        </div>
                      ) : (
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger text-white"
                             style={{ width: '24px', height: '24px' }}>
                          <FaClock size={12} />
                        </div>
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={14} className="p-0 border-0">
                      <Collapse in={openRow === idx}>
                        <div className="p-3 bg-light border-top">
                          <h6 className="mb-2 fw-bold">Détails de l'analyse :</h6>
                          <div className="bg-white p-3 rounded border">
                            <pre style={{ 
                              whiteSpace: "pre-wrap", 
                              margin: 0, 
                              fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                              fontSize: '0.85rem'
                            }}>
                              {row.explanation || "Aucun détail supplémentaire disponible."}
                            </pre>
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              </tbody>
            </Table>
          </div>

          {data.length > 0 && totalPages > 1 && (
            <div className="mt-4">
              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={5}
              />
            </div>
          )}

          {/* Modal */}
          <Modal show={showModal} onHide={handleCloseModal} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <pre style={{ whiteSpace: "pre-wrap" }}>{modalContent}</pre>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Fermer
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
}

// Ajout des PropTypes pour la validation des props
CallsTable.propTypes = {
  // Ajoutez ici les props si nécessaire
};
